import React, { useState } from 'react';

import classNames from 'classnames';

import Person from '@travauxlib/shared/src/components/DesignSystem/assets/PersonFilled.svg?react';
import { ImgLazy } from '@travauxlib/shared/src/components/Images/ImgLazy';
import type { ExtendedBrandVariants } from '@travauxlib/shared/src/types/designSystem';

export type AvatarSizes = '4xl' | '3xl' | 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs' | '3xs';

export type AvatarVariant = Exclude<ExtendedBrandVariants, 'grey'> | 'app';

export type AvatarProps = {
  className?: string;
  variant?: AvatarVariant;
  size?: AvatarSizes;
  pictureURL?: string;
  firstName?: string;
  lastName?: string;
  text?: string;
} & Omit<React.HTMLProps<HTMLDivElement>, 'size'>;

const avatarConfig: Record<
  AvatarSizes,
  {
    containerSize: string;
    avatarSize: string;
    fontSize: string;
  }
> = {
  '4xl': {
    containerSize: 'size-[15rem]',
    avatarSize: 'size-[15rem]',
    fontSize: 'text-[5rem]',
  },
  '3xl': {
    containerSize: 'size-xxl',
    avatarSize: 'size-[5rem]',
    fontSize: 'text-h3',
  },
  xxl: {
    containerSize: 'size-[2.5rem]',
    avatarSize: 'size-3xl',
    fontSize: 'text-h4',
  },
  xl: {
    containerSize: 'size-xl',
    avatarSize: 'size-[3.5rem]',
    fontSize: 'text-h5',
  },
  lg: {
    containerSize: 'size-lg',
    avatarSize: 'size-xxl',
    fontSize: 'text-b2',
  },
  md: {
    containerSize: 'size-md',
    avatarSize: 'size-xl',
    fontSize: 'text-b3',
  },
  sm: {
    containerSize: 'size-sm',
    avatarSize: 'size-lg',
    fontSize: 'text-sm',
  },
  xs: {
    containerSize: 'size-xs',
    avatarSize: 'size-xmd',
    fontSize: 'text-xs',
  },
  xxs: {
    containerSize: 'size-xs',
    avatarSize: 'size-md',
    fontSize: 'text-xs',
  },
  '3xs': {
    containerSize: 'size-xs',
    avatarSize: 'size-sm',
    fontSize: 'text-xxs',
  },
};

export const variantConfig: Record<AvatarVariant, string> = {
  primary: 'ctn-brand-medium text-inverse',
  secondary: 'ctn-secondary-medium text-inverse',
  green: 'ctn-contextual-medium text-inverse',
  blue: 'ctn-info-medium text-inverse',
  lightgreen: 'ctn-success-medium text-inverse',
  white: 'ctn-neutral-grey-weak text-inverse',
  black: 'ctn-neutral-black text-inverse',
  light: 'ctn-neutral-grey-strong text-inverse',
  app: 'ctn-brand-weak text-brand',
};

export const Avatar: React.FC<AvatarProps> = ({
  size = 'md',
  variant = 'primary',
  pictureURL,
  firstName,
  lastName,
  text,
  className,
  disabled,
  ...rest
}) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const maybeInitials = firstName && lastName ? `${firstName?.[0]}${lastName?.[0]}` : undefined;
  const latinInitials =
    maybeInitials && /^[a-zA-Z]+$/.test(maybeInitials) ? maybeInitials : undefined;

  const { avatarSize, containerSize, fontSize } = avatarConfig[size];
  const variantClasses = variantConfig[variant];

  const content = text || latinInitials;
  const showPicture = !hasError && pictureURL;

  return (
    <div
      className={classNames(
        className,
        avatarSize,
        variantClasses,
        'flex items-center justify-center rounded-full',
        { 'ctn-neutral-grey-weak! text-disabled!': disabled },
      )}
      {...rest}
    >
      {showPicture && (
        <ImgLazy
          sizes="100%"
          url={pictureURL}
          alt={lastName}
          className={classNames('rounded-full object-cover h-full', avatarSize)}
          onError={() => setHasError(true)}
        />
      )}
      {!showPicture &&
        (content ? (
          <span className={classNames('font-bold', fontSize)}>{content}</span>
        ) : (
          <Person data-testid="avatar-icon-fallback" className={containerSize} />
        ))}
    </div>
  );
};
